import { useState, useEffect } from "react";
import { api } from "../api";

const PERMISSIONS = [
  { key: "can_send_invites",    label: "Send Invites" },
  { key: "can_manage_disputes", label: "Manage Disputes" },
  { key: "can_manage_tickets",  label: "Manage Tickets" },
  { key: "can_view_customers",  label: "View Customers" },
  { key: "can_impersonate",     label: "Impersonate Customers" },
];

function PermissionCheckbox({ label, checked, onChange, disabled }) {
  return (
    <label style={{display:"flex",alignItems:"center",gap:8,fontSize:"0.88rem",cursor:disabled?"not-allowed":"pointer",opacity:disabled?0.5:1}}>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled}
        style={{width:16,height:16,accentColor:"var(--blue)"}} />
      {label}
    </label>
  );
}

// roles[] is authoritative; admin_users.role is a derived mirror and is not read.
// A super admin also carries the base "admin" role, so test with includes().
const rolesOf   = (a) => (Array.isArray(a?.roles) ? a.roles : []);
const isSuper   = (a) => rolesOf(a).includes("super_admin");
const roleLabel = (a) => {
  const r = rolesOf(a);
  if (!r.length) return "No roles";
  if (isSuper(a)) return "Super Admin";
  const extra = r.find(x => x === "billing" || x === "qc");
  return extra ? (extra === "billing" ? "Admin + Billing" : "Admin + QC") : "Admin";
};

export function SuperAdminPage() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(null);
  const [editingId, setEditingId] = useState(null);

  // New admin form
  const [newAdmin, setNewAdmin] = useState({
    email: "", name: "", password: "", role: "admin",
    can_send_invites: true, can_manage_disputes: true,
    can_manage_tickets: true, can_view_customers: true, can_impersonate: false
  });

  function load() {
    api.superAdminGetAdmins()
      .then(setAdmins)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  async function handleCreate(e) {
    e.preventDefault();
    setSaving("new");
    try {
      await api.superAdminCreateAdmin(newAdmin);
      setShowAdd(false);
      setNewAdmin({ email:"", name:"", password:"", role:"admin", can_send_invites:true, can_manage_disputes:true, can_manage_tickets:true, can_view_customers:true, can_impersonate:false });
      load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(null);
    }
  }

  async function handleTogglePermission(admin, key) {
    setSaving(admin.id);
    try {
      const updated = await api.superAdminUpdateAdmin(admin.id, { [key]: !admin[key] });
      setAdmins(prev => prev.map(a => a.id === admin.id ? { ...a, ...updated } : a));
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(null);
    }
  }

  async function handleToggleActive(admin) {
    setSaving(admin.id);
    try {
      const updated = await api.superAdminUpdateAdmin(admin.id, { is_active: !admin.is_active });
      setAdmins(prev => prev.map(a => a.id === admin.id ? { ...a, ...updated } : a));
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(null);
    }
  }

  async function handleDelete(admin) {
    if (!window.confirm(`Delete admin account for ${admin.name}? This cannot be undone.`)) return;
    setSaving(admin.id);
    try {
      await api.superAdminDeleteAdmin(admin.id);
      setAdmins(prev => prev.filter(a => a.id !== admin.id));
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(null);
    }
  }

  async function handleResetPassword(admin) {
    const pw = window.prompt(`Enter new password for ${admin.name} (min 8 characters):`);
    if (!pw) return;
    if (pw.length < 8) { alert("Password must be at least 8 characters."); return; }
    setSaving(admin.id);
    try {
      await api.superAdminUpdateAdmin(admin.id, { password: pw });
      alert("Password updated successfully.");
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(null);
    }
  }

  if (loading) return <div style={{padding:40,textAlign:"center",color:"var(--text-muted)"}}>Loading...</div>;

  return (
    <div>
      <div className="page-header" style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div>
          <div className="page-title">Admin Management</div>
          <div className="page-subtitle">Manage admin accounts and permissions</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(!showAdd)}>
          {showAdd ? "Cancel" : "+ Add Admin"}
        </button>
      </div>

      {error && <div style={{background:"#f8d7da",border:"1px solid #dc3545",borderRadius:8,padding:"10px 14px",marginBottom:16,color:"#842029"}}>{error}</div>}

      {/* Add Admin Form */}
      {showAdd && (
        <div className="card" style={{marginBottom:20}}>
          <div className="card-header"><span className="card-title">New Admin Account</span></div>
          <div className="card-body">
            <form onSubmit={handleCreate}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16,marginBottom:16}}>
                <div className="form-group" style={{margin:0}}>
                  <label>Full Name</label>
                  <input type="text" value={newAdmin.name} onChange={e => setNewAdmin(p=>({...p,name:e.target.value}))} required />
                </div>
                <div className="form-group" style={{margin:0}}>
                  <label>Email Address</label>
                  <input type="email" value={newAdmin.email} onChange={e => setNewAdmin(p=>({...p,email:e.target.value}))} required />
                </div>
                <div className="form-group" style={{margin:0}}>
                  <label>Temporary Password</label>
                  <input type="password" value={newAdmin.password} onChange={e => setNewAdmin(p=>({...p,password:e.target.value}))} required minLength={8} placeholder="Min 8 characters" />
                </div>
              </div>
              <div style={{marginBottom:16}}>
                <label style={{fontSize:"0.85rem",fontWeight:600,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:"0.08em",display:"block",marginBottom:10}}>Role</label>
                <div style={{display:"flex",gap:16}}>
                  {["admin","super_admin"].map(r => (
                    <label key={r} style={{display:"flex",alignItems:"center",gap:8,fontSize:"0.88rem",cursor:"pointer"}}>
                      <input type="radio" name="role" value={r} checked={newAdmin.role===r} onChange={() => setNewAdmin(p=>({...p,role:r}))} />
                      {r === "super_admin" ? "Super Admin" : "Admin"}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{marginBottom:20}}>
                <label style={{fontSize:"0.85rem",fontWeight:600,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:"0.08em",display:"block",marginBottom:10}}>Permissions</label>
                <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
                  {PERMISSIONS.map(p => (
                    <PermissionCheckbox key={p.key} label={p.label} checked={newAdmin[p.key]}
                      onChange={() => setNewAdmin(prev => ({...prev,[p.key]:!prev[p.key]}))} />
                  ))}
                </div>
              </div>
              <button className="btn btn-primary" type="submit" disabled={saving==="new"}>
                {saving==="new" ? "Creating..." : "Create Admin"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Admin List */}
      {admins.map(admin => (
        <div key={admin.id} className="card" style={{marginBottom:16,opacity:admin.is_active?1:0.65}}>
          <div className="card-body">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontWeight:700,fontSize:"1rem"}}>{admin.name}</span>
                  <span style={{
                    display:"inline-block",padding:"2px 8px",borderRadius:12,fontSize:"0.7rem",fontWeight:700,
                    background: isSuper(admin) ? "var(--blue)" : "var(--blue-lt)",
                    color: isSuper(admin) ? "white" : "var(--blue)"
                  }}>
                    {roleLabel(admin)}
                  </span>
                  {!admin.is_active && <span style={{background:"#f8d7da",color:"#842029",padding:"2px 8px",borderRadius:12,fontSize:"0.7rem",fontWeight:700}}>Inactive</span>}
                </div>
                <div style={{fontSize:"0.85rem",color:"var(--text-muted)",marginTop:2}}>{admin.email}</div>
                {admin.last_login_at && <div style={{fontSize:"0.78rem",color:"var(--text-muted)",marginTop:2}}>Last login: {new Date(admin.last_login_at).toLocaleDateString()}</div>}
              </div>
              <div style={{display:"flex",gap:8}}>
                <button className="btn btn-outline btn-sm" onClick={() => handleResetPassword(admin)} disabled={saving===admin.id}>
                  Reset PW
                </button>
				<button className="btn btn-outline btn-sm" onClick={async () => {
					setSaving(admin.id);
					try {
						await api.superAdminSendReset(admin.id);
						alert("Reset email sent to " + admin.email);
					} catch (err) { alert(err.message); }
					finally { setSaving(null); }
				}} disabled={saving===admin.id}>
					Send Reset Email
				</button>
                <button className="btn btn-outline btn-sm" onClick={() => handleToggleActive(admin)} disabled={saving===admin.id}>
                  {admin.is_active ? "Deactivate" : "Activate"}
                </button>
                <button className="btn btn-sm" style={{background:"#f8d7da",color:"#842029",border:"1px solid #f5c2c7",borderRadius:"var(--radius)",padding:"4px 10px",cursor:"pointer",fontSize:"0.8rem"}}
                  onClick={() => handleDelete(admin)} disabled={saving===admin.id}>
                  Delete
                </button>
              </div>
            </div>

            {/* Permissions */}
            <div style={{borderTop:"1px solid var(--border)",paddingTop:12}}>
              <div style={{fontSize:"0.72rem",fontWeight:700,letterSpacing:"0.08em",color:"var(--text-muted)",textTransform:"uppercase",marginBottom:10}}>Permissions</div>
              <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
                {PERMISSIONS.map(p => (
                  <PermissionCheckbox key={p.key} label={p.label}
                    checked={admin[p.key]}
                    onChange={() => handleTogglePermission(admin, p.key)}
                    disabled={saving===admin.id || isSuper(admin)}
                  />
                ))}
              </div>
              {isSuper(admin) && <div style={{fontSize:"0.78rem",color:"var(--text-muted)",marginTop:8,fontStyle:"italic"}}>Super admins have all permissions by default.</div>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
